import React, { useRef, useState, useEffect } from 'react';

const AudioMessagesPlayer = ({ audioSrc, histogramData, playbackTime }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedTimeMs, setElapsedTimeMs] = useState(0);
  const [progress, setProgress] = useState(0);
  const actualPlaybackTime = playbackTime ? parseFloat(playbackTime) : 0;

  // ✅ Convert percentage strings to numbers and ensure valid values
  const formattedHistogramData = Array.isArray(histogramData)
    ? histogramData.map((value) => {
        const stringValue = String(value);
        const numericValue = parseFloat(stringValue.replace('%', '')); // ✅ Strip '%' and convert to number
        return Math.round(Math.max(2, Math.min(100, numericValue))); // ✅ Clamp values between 2% and 100%
      })
    : [];

  // Toggle audio playback
  const togglePlayAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Update playback progress
  useEffect(() => {
    if (!audioRef.current) return;

    const updateProgress = () => {
      if (!audioRef.current) return;
      setElapsedTimeMs(Math.floor(audioRef.current.currentTime * 1000));
      setProgress((audioRef.current.currentTime / actualPlaybackTime) * 100);
    };

    const handleAudioEnd = () => {
      setIsPlaying(false);
      setElapsedTimeMs(0);
      setProgress(0);
    };

    if (isPlaying) {
      audioRef.current.addEventListener('timeupdate', updateProgress);
      audioRef.current.addEventListener('ended', handleAudioEnd);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateProgress);
        audioRef.current.removeEventListener('ended', handleAudioEnd);
      }
    };
  }, [isPlaying, actualPlaybackTime]);

  // ✅ Seek audio to clicked position on histogram
  const handleSeek = (e) => {
    if (!audioRef.current || actualPlaybackTime <= 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left; // Click position relative to element
    const width = rect.width; // Total width of the element
    const seekPercentage = clickX / width; // Calculate seek position
    const seekTime = seekPercentage * actualPlaybackTime; // Convert to seconds

    // ✅ Seek the audio and update progress
    audioRef.current.currentTime = seekTime;
    setElapsedTimeMs(seekTime * 1000);
    setProgress(seekPercentage * 100);
  };

  // Format playback time
  const formatPlaybackTime = (timeInMs) => {
    const totalSeconds = Math.floor(timeInMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="audio-playback-container">
      <div className="audio-playback">
        <button onClick={togglePlayAudio} className={`play-button ${isPlaying ? 'stop' : 'play'}`}>
          <div className="stop-bg-1" style={{ '--i': 1 }}></div>
          <div className="stop-bg-2" style={{ '--i': 2 }}></div>
          <div className="main-button"></div>
        </button>

        <audio ref={audioRef} src={audioSrc}></audio>

        {/* ✅ Clickable Histogram for Seeking */}
        <div className="histogram-and-timer-container">
          <div
            className="histogram-container"
            style={{ width: `${histogramData.length * 1.3 + 2.1 * (histogramData.length - 1)}px` }}
            onClick={handleSeek} // ✅ Click to seek playback
          >
            <div className="histogram-progress" style={{ width: `${progress}%` }}></div>
            {formattedHistogramData.map((value, index) => (
              <React.Fragment key={index}>
                <div className="bar-col-wrap">
                  <div className="col-cap" style={{ height: `${100 - value}%` }}></div>
                  <div className="histogram-bar" style={{ height: `${value}%` }}></div>
                </div>
                <div className="bar-gap"></div>
              </React.Fragment>
            ))}
          </div>
          {/* ✅ Updates timer correctly when seeking */}
          <span className="playback-timer">
            {isPlaying || elapsedTimeMs > 0
              ? formatPlaybackTime(elapsedTimeMs)
              : formatPlaybackTime((actualPlaybackTime || 0) * 1000)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AudioMessagesPlayer;
